// ============================================================================
//  AdminThumbnails — "Cinematographer's Selects" review wall.
//  /admin/thumbnails
//
//  Per the user brief (May 2026):
//    "Not 'thumbnail moderation tooling'. The equivalent of a
//     cinematographer reviewing daily selects."
//
//  Visual QA for the taste-system: every poor cover frame caught
//  here is a share that didn't quietly underperform. Internal only.
//
//  Layout:
//    • Slim top bar — selector mix + filter chips (All / v2 / v1 / Legacy)
//    • Grid of cinematic tiles (3 desktop, 2 tablet, 1 mobile)
//    • Each tile: cover thumb · event_type chip · selector_version
//      chip · reason ("group_moment+composed+apex_near_peak") ·
//      score · timestamp
//    • Click → modal showing chosen + up to 3 runner-up frames with
//      score breakdowns side-by-side ("did the selector have taste?")
//
//  Strict no-go: dashboards / charts / moderation actions / counts of
//  guests / admin-cluttery sidebars. Restraint is the brand.
// ============================================================================
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SELECTOR_FILTERS = [
  { id: 'all',         label: 'All' },
  { id: 'smart_v2',    label: 'v2 — Cinematic' },
  { id: 'smart_v1',    label: 'v1 — Basic' },
  { id: 'legacy_t1p5s', label: 'Legacy' },
];

const SELECTOR_ACCENT = {
  smart_v2:     { color: '#D4AF5E', glow: 'rgba(212,175,94,0.55)' },
  smart_v1:     { color: '#B89762', glow: 'rgba(184,151,98,0.45)' },
  legacy_t1p5s: { color: '#7F8593', glow: 'rgba(127,133,147,0.35)' },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const dm = (now - d.getTime()) / 60000;
  if (dm < 1) return 'Just now';
  if (dm < 60) return `${Math.floor(dm)}m ago`;
  if (dm < 60 * 24) return `${Math.floor(dm / 60)}h ago`;
  return d.toLocaleDateString();
};

const SubScorePill = ({ label, value, accent = '#D4AF5E' }) => (
  <div
    style={{
      display: 'flex', flexDirection: 'column', gap: 2,
      padding: '8px 10px', borderRadius: 10,
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${accent}26`,
      minWidth: 78,
    }}
  >
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
    }}>{label}</span>
    <span style={{
      fontSize: 14, fontWeight: 800, color: accent,
      fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
    }}>{(value ?? 0).toFixed ? value.toFixed(2) : value}</span>
  </div>
);

const CandidateCard = ({ item, label, badge, accent }) => (
  <div
    data-testid={`admin-thumb-candidate-${label.toLowerCase()}`}
    style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      padding: 12, borderRadius: 14,
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${accent}33`,
      boxShadow: `0 6px 22px ${accent}1f`,
    }}
  >
    <div style={{
      position: 'relative', width: '100%', aspectRatio: '9 / 16',
      maxHeight: 320, borderRadius: 10, overflow: 'hidden',
      background: item.url || item.thumb_url
        ? `url(${item.url || item.thumb_url}) center/cover no-repeat`
        : 'linear-gradient(135deg, #1a1626, #2d1f3e)',
    }}>
      <div style={{
        position: 'absolute', top: 8, left: 8,
        padding: '3px 9px', borderRadius: 999,
        background: `${accent}d0`, color: '#0F0F14',
        fontSize: 9, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}>{badge}</div>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontSize: 12, fontWeight: 700, color: accent,
        letterSpacing: '0.04em',
      }}>{label}</span>
      <span style={{
        fontSize: 12, fontWeight: 500,
        color: 'rgba(255,255,255,0.75)', letterSpacing: '-0.005em',
        fontFamily: 'Georgia, serif', fontStyle: 'italic',
      }}>{item.reason || '—'}</span>
      <span style={{
        fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)',
        letterSpacing: '0.06em',
      }}>
        Score {item.score?.toFixed ? item.score.toFixed(3) : (item.score ?? '—')}
        {item.source_time !== undefined || item.time !== undefined
          ? ` · t=${(item.source_time ?? item.time).toFixed?.(1) ?? '—'}s`
          : ''}
      </span>
    </div>
    <div style={{
      display: 'grid', gap: 6,
      gridTemplateColumns: 'repeat(auto-fit, minmax(78px, 1fr))',
    }}>
      <SubScorePill label="Bright"   value={item.score_breakdown?.brightness} accent={accent} />
      <SubScorePill label="Sharp"    value={item.score_breakdown?.sharpness}  accent={accent} />
      <SubScorePill label="Motion"   value={item.score_breakdown?.motion}     accent={accent} />
      <SubScorePill label="Face"     value={item.score_breakdown?.face_score} accent={accent} />
      <SubScorePill label="Thirds"   value={item.score_breakdown?.thirds_score} accent={accent} />
      <SubScorePill label="Peak +"   value={item.score_breakdown?.motion_peak_bonus} accent={accent} />
    </div>
  </div>
);

const ReviewModal = ({ item, onClose }) => {
  // Esc-to-close. Keep listeners minimal.
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  const accent = SELECTOR_ACCENT[item.selector] || SELECTOR_ACCENT.smart_v1;
  const winner = {
    url: item.thumb_url,
    reason: item.reason,
    score: item.score,
    source_time: item.source_time,
    score_breakdown: item.score_breakdown,
  };
  return (
    <motion.div
      data-testid="admin-thumb-review-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(5,3,7,0.84)',
        backdropFilter: 'blur(20px)',
        padding: 24, overflowY: 'auto',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 1240, margin: '0 auto', padding: 24, borderRadius: 22,
          background: 'linear-gradient(135deg, rgba(20,18,32,0.96) 0%, rgba(28,20,40,0.96) 100%)',
          border: `1px solid ${accent.color}44`,
          boxShadow: `0 0 60px ${accent.glow}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start',
                       justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: 'rgba(212,175,94,0.78)',
              marginBottom: 6,
            }}>
              ✦ Cinematographer's Selects
            </div>
            <h2 style={{
              margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 28, fontWeight: 400, letterSpacing: '-0.02em',
              color: '#FFFFFF',
            }}>
              {item.event_name || 'Untitled event'}
            </h2>
            <p style={{
              margin: '4px 0 0', fontSize: 12, fontWeight: 600,
              color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em',
            }}>
              {item.booth_id} · {fmtTime(item.created_at)} · {item.event_type || 'event'}
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-thumb-review-close"
            onClick={onClose}
            style={{
              padding: '8px 14px', borderRadius: 999,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: 'rgba(255,255,255,0.85)', fontSize: 12,
              fontWeight: 700, letterSpacing: '0.06em', cursor: 'pointer',
            }}
          >
            ✕ Close
          </button>
        </div>
        <div style={{
          display: 'grid', gap: 14,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}>
          <CandidateCard
            item={winner}
            label="The Pick"
            badge="✦ Chosen"
            accent={accent.color}
          />
          {(item.runners_up || []).map((ru) => (
            <CandidateCard
              key={ru.rank}
              item={ru}
              label={`Runner-up #${ru.rank}`}
              badge={`#${ru.rank}`}
              accent="#7F8593"
            />
          ))}
        </div>
        {(!item.runners_up || item.runners_up.length === 0) && (
          <p
            data-testid="admin-thumb-review-no-runners"
            style={{
              marginTop: 18, padding: 14, borderRadius: 10,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              fontSize: 13, color: 'rgba(255,255,255,0.55)',
              fontStyle: 'italic', fontFamily: 'Georgia, serif',
            }}
          >
            No runner-up frames stored for this render — likely rendered before
            the v2 capture path landed. Newer renders will show the rejected
            candidates here.
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

const AdminThumbnails = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (selectorFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (selectorFilter && selectorFilter !== 'all') params.selector = selectorFilter;
      const res = await axios.get(`${API}/admin/thumbnails`, { params });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) setError('Admin access required');
      else if (e.response?.status === 401) {
        toast.error('Please log in');
        navigate('/login');
      } else setError('Could not load review wall');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(filter); }, [load, filter]);

  const mix = data?.selectors_breakdown || {};
  const items = data?.items || [];

  const mixLine = useMemo(() => {
    const total = Object.values(mix).reduce((a, b) => a + b, 0) || 0;
    if (!total) return '—';
    const v2 = mix.smart_v2 || 0;
    const v1 = mix.smart_v1 || 0;
    const lg = mix.legacy_t1p5s || 0;
    return `v2 ${v2} · v1 ${v1} · legacy ${lg}`;
  }, [mix]);

  if (error) {
    return (
      <div
        data-testid="admin-thumb-error"
        style={{
          minHeight: '100svh',
          background: 'radial-gradient(circle at 50% 30%, #14121f 0%, #050307 70%)',
          color: 'rgba(255,255,255,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Georgia, serif', fontSize: 18,
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      data-testid="admin-thumbnails-page"
      style={{
        minHeight: '100svh',
        background: 'radial-gradient(circle at 50% 0%, #14121f 0%, #050307 70%)',
        color: '#FFFFFF',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        padding: '32px 22px 64px',
      }}
    >
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        {/* ── Header ── */}
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase', color: 'rgba(212,175,94,0.78)',
          marginBottom: 12,
        }}>
          ✦ Admin · Internal
        </div>
        <h1 style={{
          margin: 0, fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 'clamp(34px, 5vw, 52px)', fontWeight: 400,
          letterSpacing: '-0.025em', lineHeight: 1.1,
          textShadow: '0 0 60px rgba(212,175,94,0.35)',
        }}>
          Cinematographer's Selects
        </h1>
        <p style={{
          margin: '14px 0 0', maxWidth: 720,
          fontSize: 14, color: 'rgba(255,255,255,0.55)',
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          letterSpacing: '-0.005em',
        }}>
          Visual QA for the taste-system. Click a tile to see the chosen frame
          beside the runners-up — the fastest way to spot whether the
          selector chose with taste.
        </p>

        {/* ── Filter row + selector mix ── */}
        <div style={{
          marginTop: 28, display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', gap: 10, justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SELECTOR_FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`admin-thumb-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  style={{
                    padding: '8px 16px', borderRadius: 999,
                    background: active
                      ? 'rgba(212,175,94,0.18)'
                      : 'rgba(255,255,255,0.04)',
                    border: active
                      ? '1px solid rgba(212,175,94,0.7)'
                      : '1px solid rgba(255,255,255,0.12)',
                    color: active ? '#D4AF5E' : 'rgba(255,255,255,0.78)',
                    fontSize: 12, fontWeight: 800, letterSpacing: '0.04em',
                    cursor: 'pointer',
                    boxShadow: active ? '0 0 18px rgba(212,175,94,0.3)' : 'none',
                    transition: 'background 0.2s, border 0.2s, box-shadow 0.2s',
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <div
            data-testid="admin-thumb-mix"
            style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
            }}
          >
            ✦ mix · {mixLine} · {data?.total_ready || 0} total
          </div>
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div
            data-testid="admin-thumb-loading"
            style={{
              marginTop: 60, textAlign: 'center',
              fontSize: 14, color: 'rgba(255,255,255,0.5)',
              letterSpacing: '0.18em', textTransform: 'uppercase',
              fontFamily: 'Georgia, serif',
            }}
          >
            ✦ Loading selects…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-thumb-empty"
            style={{
              marginTop: 80, textAlign: 'center',
              fontSize: 16, color: 'rgba(255,255,255,0.55)',
              fontStyle: 'italic', fontFamily: 'Georgia, serif',
            }}
          >
            No selects match this filter yet — first cinematic moments will appear here.
          </div>
        ) : (
          <div
            style={{
              marginTop: 26,
              display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            {items.map((it) => {
              const accent = SELECTOR_ACCENT[it.selector]
                          || SELECTOR_ACCENT.smart_v1;
              return (
                <motion.button
                  key={it.booth_id}
                  type="button"
                  data-testid={`admin-thumb-tile-${it.booth_id}`}
                  onClick={() => setSelected(it)}
                  whileHover={{ y: -3 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 22 }}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 10,
                    padding: 12, borderRadius: 18,
                    background: 'linear-gradient(135deg, rgba(20,18,32,0.92) 0%, rgba(32,20,40,0.92) 100%)',
                    border: `1px solid ${accent.color}3a`,
                    boxShadow: `0 6px 22px ${accent.glow}`,
                    cursor: 'pointer', textAlign: 'left',
                    color: 'inherit',
                  }}
                >
                  <div style={{
                    position: 'relative', width: '100%', aspectRatio: '9 / 16',
                    borderRadius: 12, overflow: 'hidden',
                    background: it.thumb_url
                      ? `url(${it.thumb_url}) center/cover no-repeat`
                      : 'linear-gradient(135deg, #1a1626, #2d1f3e)',
                  }}>
                    <div style={{
                      position: 'absolute', top: 8, left: 8,
                      padding: '3px 9px', borderRadius: 999,
                      background: `${accent.color}d0`, color: '#0F0F14',
                      fontSize: 9, fontWeight: 800, letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}>
                      {it.selector || 'unknown'}
                    </div>
                    {(it.runners_up?.length || 0) > 0 && (
                      <div style={{
                        position: 'absolute', bottom: 8, right: 8,
                        padding: '3px 9px', borderRadius: 999,
                        background: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(8px)',
                        color: '#FFFFFF', fontSize: 9, fontWeight: 700,
                        letterSpacing: '0.06em',
                        border: '1px solid rgba(255,255,255,0.18)',
                      }}>
                        +{it.runners_up.length} selects
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: '#FFFFFF',
                      letterSpacing: '-0.005em',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {it.event_name || 'Untitled event'}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: accent.color,
                      letterSpacing: '0.04em',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {it.reason || '—'}
                    </span>
                    <div style={{
                      display: 'flex', gap: 8, alignItems: 'center',
                      fontSize: 10, fontWeight: 700,
                      color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}>
                      <span>{it.event_type || 'event'}</span>
                      <span>·</span>
                      <span>Score {it.score?.toFixed?.(3) ?? '—'}</span>
                      <span>·</span>
                      <span>{fmtTime(it.created_at)}</span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
      <AnimatePresence>
        {selected && (
          <ReviewModal item={selected} onClose={() => setSelected(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminThumbnails;
